import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDepartments } from '../../../hooks/useDepartments';
import { projectService } from '../../../services/projectService';
import { departmentSubmissionService } from '../../../services/departmentSubmissionService';
import LoadingSpinner from '../../common/LoadingSpinner';
import { EmptyState } from '../../common/EmptyState';
import UniversalFilters from '../../common/UniversalFilters';
import ExportExcelButton from '../../common/ExportExcelButton';
import { getFilterConfig } from '../../../constants/filterConfigs';
import { useFilters } from '../../../hooks/useFilters';
import ProjectDetailsModal from './ProjectDetailsModal';
import ProjectApprovalModal from './ProjectApprovalModal';
import ProjectEditModal from './ProjectEditModal';
import StatusDropdown from './StatusDropdown';
import {
  PROJECT_STATUS,
  formatFinancialRequirement,
  formatPriority
} from '../../../constants/projectConstants';
import { getSectorLabel, USER_ROLES } from '../../../constants/authConstants';
import { dateUtils, arrayUtils, financialYearUtils } from '../../../utils/helpers';
import { exportProjectApprovalToExcel } from '../../../utils/excelExport';

const ProjectApproval = () => {
  const { user, getUserRole } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();
  const { departments, loading: loadingDepartments, getDepartmentLabel } = useDepartments();
  
  const userRole = getUserRole();
  const isAdmin = userRole === USER_ROLES.ADMIN;
  const isPlanningOfficial = userRole === USER_ROLES.PLANNING_OFFICIAL;
  
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lockedDepartments, setLockedDepartments] = useState([]);
  
  const {
    filters,
    handleFilterChange,
    clearFilters,
    hasActiveFilters
  } = useFilters({
    search: '',
    financialYear: financialYearUtils.getCurrentFinancialYear(),
    sector: '',
    department: '',
    status: '',
    sort: 'priority-asc'
  });

  const [actionLoading, setActionLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedProjectForDetails, setSelectedProjectForDetails] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedProjectForApproval, setSelectedProjectForApproval] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProjectForEdit, setSelectedProjectForEdit] = useState(null);
  
  const [deletingProject, setDeletingProject] = useState(null);
  const [exportingExcel, setExportingExcel] = useState(false);

  const filterConfig = getFilterConfig('PROJECT_APPROVAL');

  const titleSize = getResponsiveValue({
    mobile: 'text-xl',
    desktop: 'text-2xl'
  });

  const cardPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  useEffect(() => {
    loadProjects();
  }, [filters.financialYear]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [projects, filters, lockedDepartments]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      
      // Load all projects
      const result = await projectService.getAllProjects();
      
      if (result.success) {
        let allProjects = result.data;
        
        // NEW: For admin, filter by locked departments
        if (isAdmin && filters.financialYear) {
          const lockedDepts = await departmentSubmissionService.getLockedDepartments(filters.financialYear);
          setLockedDepartments(lockedDepts.data);
          
          // Only show projects from departments that have locked submissions
          allProjects = allProjects.filter(project => {
            // Show project if:
            // 1. It's from the selected financial year AND
            // 2. Its department has a locked submission
            return project.financialYear === filters.financialYear && 
                   lockedDepts.data.includes(project.department);
          });
        }
        
        setProjects(allProjects);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = useCallback(() => {
    let filtered = [...projects];

    // Apply filters first
    if (filters.search && filters.search.trim()) {
      const searchLower = filters.search.toLowerCase().trim();
      filtered = filtered.filter(project => {
        const deptLabel = getDepartmentLabel(project.department).toLowerCase();
        const projectName = project.projectName.toLowerCase();
        const brief = project.brief?.toLowerCase() || '';
        const sector = project.sector?.toLowerCase() || '';
        const fy = project.financialYear?.toLowerCase() || '';
        
        return deptLabel.includes(searchLower) || 
               projectName.includes(searchLower) || 
               brief.includes(searchLower) ||
               sector.includes(searchLower) ||
               fy.includes(searchLower);
      });
    }

    if (filters.financialYear) {
      filtered = filtered.filter(project => project.financialYear === filters.financialYear);
    }

    if (filters.status) {
      filtered = filtered.filter(project => project.status === filters.status);
    }

    if (filters.sector) {
      filtered = filtered.filter(project => project.sector === filters.sector);
    }

    if (filters.department) {
      filtered = filtered.filter(project => project.department === filters.department);
    }

    // Two-level sorting: 
    // 1. Primary: Sort departments alphabetically
    // 2. Secondary: Within each department, sort by priority (ascending)
    filtered.sort((a, b) => {
      // Get department labels for comparison
      const deptA = getDepartmentLabel(a.department).toLowerCase();
      const deptB = getDepartmentLabel(b.department).toLowerCase();
      
      // Compare departments alphabetically
      const deptComparison = deptA.localeCompare(deptB);
      
      // If departments are different, sort by department name
      if (deptComparison !== 0) {
        return deptComparison;
      }
      
      // If same department, sort by priority (ascending - lower number = higher priority)
      const aPriority = a.priority ? parseInt(a.priority) : 999999;
      const bPriority = b.priority ? parseInt(b.priority) : 999999;
      
      return aPriority - bPriority;
    });

    setFilteredProjects(filtered);
  }, [projects, filters.search, filters.financialYear, filters.status, filters.sector, filters.department, getDepartmentLabel, lockedDepartments]);

  const handleReviewProject = (project) => {
    setSelectedProjectForApproval(project);
    setShowApprovalModal(true);
  };

  const handleProjectApproval = async (projectId, newStatus, approvedAmount = null) => {
    try {
      setActionLoading(true);
      
      const result = await projectService.updateProjectStatusWithAmount(
        projectId, 
        newStatus, 
        approvedAmount,
        user.uid
      );
      
      if (result.success) {
        setProjects(prev => prev.map(proj => 
          proj.id === projectId 
            ? { 
                ...proj, 
                status: newStatus,
                approvedAmount: approvedAmount,
                updatedAt: new Date(),
                approvedBy: user.uid,
                approvedAt: new Date()
              }
            : proj
        ));
        
        const project = projects.find(p => p.id === projectId);
        const actionText = newStatus === PROJECT_STATUS.APPROVED ? 'approved' : 'rejected';
        setSuccessMessage(`Project "${project?.projectName}" has been ${actionText} successfully.`);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 5000);
      }
    } catch (error) {
      console.error('Error updating project status:', error);
      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = async (projectId, newStatus, approvedAmount = null) => {
    try {
      setActionLoading(true);
      
      const result = await projectService.updateProjectStatusWithAmount(
        projectId, 
        newStatus, 
        approvedAmount,
        user.uid
      );
      
      if (result.success) {
        setProjects(prev => prev.map(project => 
          project.id === projectId 
            ? { 
                ...project, 
                status: newStatus,
                approvedAmount: approvedAmount ? parseFloat(approvedAmount) : project.approvedAmount,
                updatedAt: new Date(),
                approvedBy: user.uid,
                approvedAt: new Date()
              }
            : project
        ));
        
        const project = projects.find(p => p.id === projectId);
        const actionText = newStatus === PROJECT_STATUS.APPROVED ? 'approved' : 'rejected';
        setSuccessMessage(`Project "${project?.projectName}" has been ${actionText} successfully.`);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 5000);
      }
    } catch (error) {
      console.error('Error changing project status:', error);
      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditProject = (project) => {
    setSelectedProjectForEdit(project);
    setShowEditModal(true);
  };

  const handleUpdateProject = async (projectId, updatedData) => {
    try {
      const result = await projectService.updateProject(projectId, updatedData, user.uid);
      
      if (result.success) {
        setProjects(prev => prev.map(p => 
          p.id === projectId 
            ? { ...p, ...updatedData, updatedAt: new Date() }
            : p
        ));
        
        setSuccessMessage(result.message);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  };

  const handleDeleteProject = async (projectId, projectName) => {
    if (!isAdmin) {
      alert('Only administrators can delete projects.');
      return;
    }

    if (!confirm(`Are you sure you want to delete "${projectName}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingProject(projectId);
      const result = await projectService.deleteProject(projectId);
      
      if (result.success) {
        setProjects(prev => prev.filter(p => p.id !== projectId));
        setSuccessMessage(`Project "${projectName}" deleted successfully.`);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project: ' + error.message);
    } finally {
      setDeletingProject(null);
    }
  };

  const handleRowClick = (project) => {
    setSelectedProjectForDetails(project);
    setShowDetailsModal(true);
  };

  const handleExportExcel = () => {
    try {
      setExportingExcel(true);
      
      const exportData = {
        projects: filteredProjects
      };
      
      exportProjectApprovalToExcel(exportData, filters, getDepartmentLabel, getSectorLabel);
      
      setSuccessMessage('Excel exported successfully!');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Failed to export Excel: ' + error.message);
    } finally {
      setExportingExcel(false);
    }
  };

  if (loading || loadingDepartments) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading projects..." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showSuccessMessage && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3 shadow-sm">
          <div className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-green-700 font-medium text-sm">{successMessage}</span>
          </div>
        </div>
      )}

      {/* NEW: Info banner for admin showing filtered view */}
      {isAdmin && filters.financialYear && lockedDepartments.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
          <div className="flex items-center">
            <svg className="w-4 h-4 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span className="text-blue-700 text-sm">
              Showing projects only from departments with locked submissions ({lockedDepartments.length} {lockedDepartments.length === 1 ? 'department' : 'departments'})
            </span>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className={`${titleSize} font-bold text-text-primary mb-1`}>Project Approval</h1>
          <p className="text-text-secondary text-sm">
            {isPlanningOfficial 
              ? 'View project submissions from all departments (Read-only)'
              : 'Review and approve project submissions from departments with locked submissions'
            }
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <ExportExcelButton 
            onClick={handleExportExcel}
            loading={exportingExcel}
            disabled={filteredProjects.length === 0}
          />
          
          <UniversalFilters
            config={filterConfig}
            values={filters}
            onChange={handleFilterChange}
            onClearFilters={clearFilters}
            mode="dropdown"
            dropdownProps={{
              buttonProps: { size: 'md' },
              contentProps: { layout: 'grid' }
            }}
          />
        </div>
      </div>

      {hasActiveFilters && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
          <div className="flex items-center">
            <svg className="w-4 h-4 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span className="text-blue-700 text-sm">
              Showing <span className="font-semibold">{filteredProjects.length}</span> of <span className="font-semibold">{projects.length}</span> projects
            </span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {filteredProjects.length === 0 ? (
          <div className={cardPadding}>
            <EmptyState
              icon={
                <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
              title={projects.length === 0 ? "No projects submitted yet" : "No projects match your filters"}
              description={
                projects.length === 0
                  ? isAdmin 
                    ? "Projects will appear here once departments lock and submit their proposals"
                    : "Project submissions will appear here for approval"
                  : "Try adjusting your search or filter criteria"
              }
              actionLabel={hasActiveFilters ? "Clear Filters" : null}
              onAction={hasActiveFilters ? clearFilters : null}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-16">S. No.</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-40">Department</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-42">Sector</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-48">Project Name</th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-20">Financial Year</th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-24">Proposed Total Cost<br /><span className="text-gray-500 normal-case font-normal">(in lakh)</span></th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-24">Approved Total Cost<br /><span className="text-gray-500 normal-case font-normal">(in lakh)</span></th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-20">
                    Dept. Priority
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-20">Submitted</th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-32">
                    {isAdmin ? 'Status / Actions' : 'Status'}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredProjects.map((project, index) => (
                  <tr 
                    key={project.id}
                    className="hover:bg-blue-50 transition-colors duration-200 cursor-pointer"
                    onClick={() => handleRowClick(project)}
                  >
                    <td className="px-3 py-3 text-xs text-gray-600 font-semibold border-r border-gray-200">
                      <div className="flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full text-xs font-bold">{index + 1}</div>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-900 font-semibold border-r border-gray-200">{getDepartmentLabel(project.department)}</td>
                    <td className="px-3 py-3 text-xs text-gray-600 border-r border-gray-200">{getSectorLabel(project.sector)}</td>
                    <td className="px-3 py-3 text-xs text-gray-900 font-medium border-r border-gray-200">
                      <div className="max-w-xs">
                        <div className="font-medium line-clamp-2 hover:text-blue-600 transition-colors">{project.projectName}</div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center text-xs text-gray-600 border-r border-gray-200">{project.financialYear ? `FY ${project.financialYear}` : 'N/A'}</td>
                    <td className="px-3 py-3 text-center text-xs text-gray-900 font-medium border-r border-gray-200">{formatFinancialRequirement(project.financialRequirement)}</td>
                    <td className="px-3 py-3 text-center text-xs font-medium border-r border-gray-200">
                      {project.status === PROJECT_STATUS.APPROVED && project.approvedAmount
                        ? <span className="text-green-600">{formatFinancialRequirement(project.approvedAmount)}</span>
                        : <span className="text-gray-400">—</span>
                      }
                    </td>
                    <td className="px-3 py-3 text-center border-r border-gray-200">
                      {project.priority ? (
                        <div className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-full font-bold text-xs">
                          {formatPriority(project.priority)}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-center text-xs text-gray-600 border-r border-gray-200">{dateUtils.formatDate(project.createdAt)}</td>
                    <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      {isAdmin ? (
                        <div className="flex items-center justify-center space-x-2">
                          {project.status === PROJECT_STATUS.PENDING ? (
                            <>
                              <button
                                onClick={() => handleReviewProject(project)}
                                className="px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={actionLoading}
                                title="Review Project"
                              >
                                Review
                              </button>
                              <button
                                onClick={() => handleEditProject(project)}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Edit project"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                            </>
                          ) : (
                            <StatusDropdown
                              project={project}
                              onStatusChange={handleStatusChange}
                              disabled={actionLoading}
                              onApprovalRequest={handleReviewProject}
                            />
                          )}
                          <button
                            onClick={() => handleDeleteProject(project.id, project.projectName)}
                            disabled={deletingProject === project.id}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Delete project"
                          >
                            {deletingProject === project.id ? (
                              <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-red-600"></div>
                            ) : (
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        </div>
                      ) : (
                        <StatusDropdown
                          project={project}
                          onStatusChange={() => {}}
                          disabled={true}
                          onApprovalRequest={null}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ProjectDetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedProjectForDetails(null);
        }}
        project={selectedProjectForDetails}
      />

      {isAdmin && (
        <>
          <ProjectApprovalModal
            isOpen={showApprovalModal}
            onClose={() => {
              setShowApprovalModal(false);
              setSelectedProjectForApproval(null);
            }}
            project={selectedProjectForApproval}
            onApprove={handleProjectApproval}
            loading={actionLoading}
          />

          <ProjectEditModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setSelectedProjectForEdit(null);
            }}
            project={selectedProjectForEdit}
            onUpdate={handleUpdateProject}
          />
        </>
      )}
    </div>
  );
};

export default ProjectApproval;